import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    LinearProgress,
    Divider
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

function AttendanceMetrics({ userId, orgId }) {
    const [metrics, setMetrics] = useState({
        totalWorkDays: 0,
        daysPresent: 0,
        daysAbsent: 0,
        lateArrivals: 0,
        earlyDepartures: 0,
        perfectAttendanceDays: 0,
        attendanceRate: 0,
        punctualityRate: 0,
        averageHoursPerDay: 0,
        lastCheckInTime: null,
        averageClockInTime: null,
        recentLogs: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const calculateAttendanceMetrics = async () => {
            setLoading(true);
            setError(null);

            try {
                // Get the first day of the current month
                const today = new Date();
                const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                const firstDayOfMonthTimestamp = Timestamp.fromDate(firstDayOfMonth);

                // Fetch time logs for the current month
                const logsRef = collection(db, "organizations", orgId, "timeLogs");
                const q = query(
                    logsRef,
                    where("userId", "==", userId),
                    where("timestamp", ">=", firstDayOfMonthTimestamp),
                    orderBy("timestamp", "asc")
                );

                const snapshot = await getDocs(q);
                const timeLogs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp?.toDate() || null
                }));

                // Process logs to calculate metrics
                const workDayMap = new Map();
                let totalHoursWorked = 0;
                let lateArrivals = 0;
                let earlyDepartures = 0;
                let perfectDays = 0;
                let clockInTimes = [];
                let lastClockIn = null;

                // Expected work hours (e.g., 9:00 AM to 5:00 PM)
                const expectedStartHour = 9; // 9:00 AM
                const expectedEndHour = 17;  // 5:00 PM
                const lateThresholdMinutes = 15; // 15 minutes grace period
                const earlyDepartureThresholdMinutes = 15; // 15 minutes early departure threshold

                // Group logs by day
                timeLogs.forEach(log => {
                    if (!log.timestamp) return;

                    const date = log.timestamp.toDateString();

                    if (!workDayMap.has(date)) {
                        workDayMap.set(date, {
                            date: log.timestamp,
                            logs: []
                        });
                    }

                    workDayMap.get(date).logs.push(log);

                    if (log.type === "clockIn") {
                        clockInTimes.push(log.timestamp);
                        lastClockIn = log.timestamp;
                    }
                });

                // Calculate metrics for each work day
                workDayMap.forEach((dayData) => {
                    const { logs } = dayData;
                    const clockInLogs = logs.filter(log => log.type === "clockIn");
                    const clockOutLogs = logs.filter(log => log.type === "clockOut");

                    if (clockInLogs.length > 0 && clockOutLogs.length > 0) {
                        // Sort logs by timestamp
                        const sortedClockIns = clockInLogs.sort((a, b) => a.timestamp - b.timestamp);
                        const sortedClockOuts = clockOutLogs.sort((a, b) => a.timestamp - b.timestamp);

                        const firstClockIn = sortedClockIns[0].timestamp;
                        const lastClockOut = sortedClockOuts[sortedClockOuts.length - 1].timestamp;

                        // Check if late arrival
                        const expectedStartTime = new Date(firstClockIn);
                        expectedStartTime.setHours(expectedStartHour, 0, 0, 0);

                        const lateThreshold = new Date(expectedStartTime);
                        lateThreshold.setMinutes(lateThreshold.getMinutes() + lateThresholdMinutes);

                        if (firstClockIn > lateThreshold) {
                            lateArrivals++;
                        }

                        // Check if early departure
                        const expectedEndTime = new Date(lastClockOut);
                        expectedEndTime.setHours(expectedEndHour, 0, 0, 0);

                        const earlyThreshold = new Date(expectedEndTime);
                        earlyThreshold.setMinutes(earlyThreshold.getMinutes() - earlyDepartureThresholdMinutes);

                        if (lastClockOut < earlyThreshold) {
                            earlyDepartures++;
                        }

                        // Calculate hours worked
                        const hoursWorked = (lastClockOut - firstClockIn) / (1000 * 60 * 60);
                        totalHoursWorked += hoursWorked;

                        // Check for perfect attendance day
                        if (firstClockIn <= lateThreshold && lastClockOut >= earlyThreshold) {
                            perfectDays++;
                        }
                    }
                });

                // Calculate average clock-in time
                let averageClockInTime = null;
                if (clockInTimes.length > 0) {
                    // Convert all clock-in times to minutes since midnight
                    const minutesSinceMidnight = clockInTimes.map(time => {
                        return time.getHours() * 60 + time.getMinutes();
                    });

                    // Calculate average minutes since midnight
                    const avgMinutes = minutesSinceMidnight.reduce((sum, mins) => sum + mins, 0) / clockInTimes.length;

                    // Convert back to hours and minutes
                    const avgHours = Math.floor(avgMinutes / 60);
                    const avgMins = Math.round(avgMinutes % 60);

                    // Format as a string (e.g., "9:05 AM")
                    const tempDate = new Date();
                    tempDate.setHours(avgHours, avgMins, 0, 0);
                    averageClockInTime = tempDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                }

                // Calculate final metrics
                const totalWorkDays = workDayMap.size;
                const daysPresent = workDayMap.size;
                const businessDaysInMonth = getBusinessDaysInMonth(today.getFullYear(), today.getMonth());
                const daysAbsent = Math.max(0, getWorkdaysPassedThisMonth() - daysPresent);
                const attendanceRate = totalWorkDays > 0 ? (daysPresent / businessDaysInMonth) * 100 : 0;
                const punctualityRate = daysPresent > 0 ? ((daysPresent - lateArrivals) / daysPresent) * 100 : 0;
                const averageHoursPerDay = daysPresent > 0 ? totalHoursWorked / daysPresent : 0;

                setMetrics({
                    totalWorkDays,
                    daysPresent,
                    daysAbsent,
                    lateArrivals,
                    earlyDepartures,
                    perfectAttendanceDays: perfectDays,
                    attendanceRate,
                    punctualityRate,
                    averageHoursPerDay,
                    lastCheckInTime: lastClockIn,
                    averageClockInTime,
                    recentLogs: timeLogs.slice(0, 10)
                });
            } catch (err) {
                console.error("Error calculating attendance metrics:", err);
                setError("Failed to calculate attendance metrics. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        calculateAttendanceMetrics();
    }, [userId, orgId]);

    // Helper function to get the number of business days in a month
    const getBusinessDaysInMonth = (year, month) => {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        let count = 0;
        const currentDate = new Date(firstDay);

        while (currentDate <= lastDay) {
            const dayOfWeek = currentDate.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                count++;
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return count;
    };

    // Helper function to get the number of workdays that have passed in the current month
    const getWorkdaysPassedThisMonth = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

        let count = 0;
        const currentDate = new Date(firstDay);

        while (currentDate <= today) {
            const dayOfWeek = currentDate.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                count++;
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return count;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Attendance Metrics
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Your attendance and punctuality statistics for the current month.
            </Typography>

            <Grid container spacing={3}>
                {/* Attendance Summary Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Monthly Attendance
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ flexGrow: 1, mr: 2 }}>
                                    <Typography variant="body2" gutterBottom>
                                        Attendance Rate
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={metrics.attendanceRate}
                                        color={metrics.attendanceRate >= 90 ? "success" : metrics.attendanceRate >= 75 ? "warning" : "error"}
                                        sx={{ height: 8, borderRadius: 1 }}
                                    />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {Math.round(metrics.attendanceRate)}%
                                </Typography>
                            </Box>

                            <Box sx={{ my: 3 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Days Present
                                        </Typography>
                                        <Typography variant="h6">
                                            {metrics.daysPresent}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Days Absent
                                        </Typography>
                                        <Typography variant="h6">
                                            {metrics.daysAbsent}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Perfect Days
                                        </Typography>
                                        <Typography variant="h6">
                                            {metrics.perfectAttendanceDays}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="body2" gutterBottom>
                                Monthly work days: {getBusinessDaysInMonth(new Date().getFullYear(), new Date().getMonth())}
                            </Typography>
                            <Typography variant="body2">
                                Completed so far: {metrics.daysPresent} days
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Punctuality Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Punctuality Metrics
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ flexGrow: 1, mr: 2 }}>
                                    <Typography variant="body2" gutterBottom>
                                        On-time Rate
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={metrics.punctualityRate}
                                        color={metrics.punctualityRate >= 90 ? "success" : metrics.punctualityRate >= 75 ? "warning" : "error"}
                                        sx={{ height: 8, borderRadius: 1 }}
                                    />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {Math.round(metrics.punctualityRate)}%
                                </Typography>
                            </Box>

                            <Box sx={{ my: 3 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Late Arrivals
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: metrics.lateArrivals > 3 ? 'error.main' : 'text.primary' }}>
                                            {metrics.lateArrivals}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Early Leaves
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: metrics.earlyDepartures > 3 ? 'error.main' : 'text.primary' }}>
                                            {metrics.earlyDepartures}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Avg Hours/Day
                                        </Typography>
                                        <Typography variant="h6">
                                            {metrics.averageHoursPerDay.toFixed(1)}h
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                    Average clock-in time: {metrics.averageClockInTime || 'N/A'}
                                </Typography>
                            </Box>

                            {metrics.lastCheckInTime && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                    <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                        Last clock-in: {metrics.lastCheckInTime.toLocaleString()}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Attendance Status Card */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, boxShadow: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {metrics.attendanceRate >= 90 ? (
                                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                            ) : (
                                <WarningIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                            )}

                            <Box>
                                <Typography variant="h6">
                                    {metrics.attendanceRate >= 90
                                        ? "Great job! Your attendance is excellent."
                                        : metrics.attendanceRate >= 75
                                            ? "Your attendance is good, but could be improved."
                                            : "Your attendance needs improvement."}
                                </Typography>

                                <Typography variant="body2" color="text.secondary">
                                    {metrics.punctualityRate >= 90
                                        ? "You've been consistently punctual this month."
                                        : "Try to improve your punctuality to avoid late arrivals."}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default AttendanceMetrics;